import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { useMe } from '@/features/auth/useAuth'
import { Dialog, Field, GhostButton, INPUT, PrimaryButton } from '@/features/inventory/shared'
import { fmtDate, fmtEur, fmtNumber } from '@/i18n'
import type { ProjectWorkflow } from '@/features/workflow/types'
import { REASONS, STATES, type OutputLine, type ProjectExecution, type Reason, type State } from './types'
import { STATE_BG, STRIPES, StateChip, reasonLabel, stateLabel, useExecT, type ExecT } from './shared'

const todayIso = () => new Date().toISOString().slice(0, 10)
const dayNum = (iso: string) => Math.floor(Date.parse(`${iso}T12:00:00Z`) / 86_400_000)

/**
 * The project's lifecycle on its page (docs/project-execution-design.md): state and how long,
 * a timeline with blocked periods and the planned end, invoiced/paid against the contract, and
 * what was built against the plan. Admins record states, blocks and the plan.
 */
export function ExecutionPanel({ projectId }: { projectId: string }) {
  const t = useExecT()
  const { data: me } = useMe()
  const admin = me?.isAdmin ?? false
  // The Project Manager, the CEO or an admin records blocks and the plan (docs/project-workflow-design.md).
  const { data: wf } = useQuery<ProjectWorkflow>({
    queryKey: ['workflow', projectId], queryFn: () => api.get<ProjectWorkflow>(`/api/projects/${projectId}/workflow`),
  })
  const manage = admin || (wf?.me.canManage ?? false)
  const { data } = useQuery<ProjectExecution>({
    queryKey: ['execution', projectId], queryFn: () => api.get<ProjectExecution>(`/api/projects/${projectId}/execution`),
  })
  const [dialog, setDialog] = useState<'state' | 'block' | 'endBlock' | 'plan' | null>(null)
  if (!data) return null
  const x = data
  const late = x.actuals.daysLate

  return (
    <section className="space-y-4 rounded-2xl bg-white p-4 ring-1 ring-neutral-200 sm:p-5 dark:bg-neutral-900 dark:ring-neutral-800">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1.5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{t('panelTitle')}</h2>
          <div className="flex flex-wrap items-center gap-2">
            <StateChip state={x.state} size="lg" />
            {x.stateSince && <span className="text-sm text-neutral-600 dark:text-neutral-300">{t('since', { date: fmtDate(x.stateSince) })} · <b>{t('days', { n: x.daysInState })}</b></span>}
            {x.derived && x.state && <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300" title={t('derivedHint')}>{t('derivedBadge')}</span>}
          </div>
          {x.blockedNow && (
            <div className="inline-flex items-center gap-2 rounded-lg bg-red-50 px-2 py-1 text-sm font-medium text-red-800 dark:bg-red-500/10 dark:text-red-200">
              <span className="inline-block h-3 w-5 rounded-sm" style={STRIPES} />
              {t('blockedSince', { date: fmtDate(x.blockedNow.startsOn), reason: reasonLabel(t, x.blockedNow.reason) })}
            </div>
          )}
          {x.idleDays != null && <div className="inline-flex rounded-lg bg-neutral-100 px-2 py-1 text-sm font-medium text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">{t('idle', { n: x.idleDays })}</div>}
          {x.derived && x.state && <p className="max-w-xl text-xs text-neutral-500">{t('derivedHint')}</p>}
        </div>
        {manage && (
          <div className="flex flex-wrap gap-2">
            {admin && <PrimaryButton onClick={() => setDialog('state')}>{t('changeState')}</PrimaryButton>}
            {x.blockedNow ? <GhostButton onClick={() => setDialog('endBlock')}>{t('endBlock')}</GhostButton> : <GhostButton onClick={() => setDialog('block')}>{t('block')}</GhostButton>}
            <GhostButton onClick={() => setDialog('plan')}>{t('editPlan')}</GhostButton>
          </div>
        )}
      </div>

      {x.timeline.length === 0 ? <p className="text-sm text-neutral-500">{t('noTimeline')}</p> : <Ribbon t={t} x={x} />}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label={t('kpiInvoiced')} value={fmtEur(x.revenue.invoiced)}
          sub={x.plan.contractValue ? t('kpiContract', { value: fmtEur(x.plan.contractValue) }) : t('kpiNoContract')}
          pct={x.plan.contractValue ? x.revenue.invoiced / x.plan.contractValue : null} source={x.plan.sources.contractValue} />
        <Kpi label={t('kpiPaid')} value={fmtEur(x.revenue.paid)} sub={`${t('kpiOwed')} ${fmtEur(x.revenue.owed)}${x.revenue.unknown > 0 ? ` · ${t('kpiUnknown')} ${fmtEur(x.revenue.unknown)}` : ''}`} />
        <Kpi label={t('kpiSchedule')} value={late == null ? '—' : late > 0 ? t('daysLate', { n: late }) : t('onTime')}
          sub={x.plan.endOn ? t('plannedEnd', { date: fmtDate(x.plan.endOn) }) : t('noPlanEnd')} tone={late != null && late > 0 ? 'bad' : undefined} source={x.plan.sources.endOn} />
        <Kpi label={t('kpiState')} value={t('days', { n: x.daysInState })}
          sub={x.timeline.length ? t('blockedDays', { n: x.timeline.reduce((s, g) => s + g.blockedDays, 0) }) : ''} />
      </div>
      {x.revenue.paidAsOf && <p className="text-xs text-neutral-500">{t('paidAsOf', { date: fmtDate(x.revenue.paidAsOf) })}</p>}

      <div className="grid gap-4 lg:grid-cols-2">
        <OutputTable t={t} lines={x.output} />
        <RevenueTable t={t} x={x} />
      </div>

      <History t={t} x={x} admin={admin} />

      {dialog === 'state' && <StateDialog t={t} projectId={projectId} current={x.state} onClose={() => setDialog(null)} />}
      {dialog === 'block' && <BlockDialog t={t} projectId={projectId} onClose={() => setDialog(null)} />}
      {dialog === 'endBlock' && x.blockedNow && <EndBlockDialog t={t} projectId={projectId} blockId={x.blockedNow.id} starts={x.blockedNow.startsOn} onClose={() => setDialog(null)} />}
      {dialog === 'plan' && <PlanDialog t={t} x={x} onClose={() => setDialog(null)} />}
    </section>
  )
}

function Kpi({ label, value, sub, pct, tone, source }: { label: string; value: string; sub?: string; pct?: number | null; tone?: 'bad' | undefined; source?: 'app' | 'au' | undefined }) {
  const t = useExecT()
  return (
    <div className="rounded-xl bg-neutral-50 p-3 dark:bg-neutral-800/50">
      <div className="text-xs text-neutral-500">{label}</div>
      <div className={`tabular mt-0.5 text-xl font-semibold ${tone === 'bad' ? 'text-red-700 dark:text-red-300' : ''}`}>{value}</div>
      {pct != null && <div className="mt-1.5 h-1.5 rounded-full bg-neutral-200 dark:bg-neutral-700"><div className="h-full rounded-full bg-brand-600" style={{ width: `${Math.min(100, pct * 100)}%` }} /></div>}
      {sub && <div className="mt-1 text-xs text-neutral-500">{sub}{source === 'au' ? ` (${t('fromAu')})` : ''}</div>}
    </div>
  )
}

/** States as coloured spans over time; blocked periods striped on top; the planned end as a marker. */
function Ribbon({ t, x }: { t: ExecT; x: ProjectExecution }) {
  const start = dayNum(x.timeline[0]!.from)
  const end = Math.max(dayNum(todayIso()), x.plan.endOn ? dayNum(x.plan.endOn) : 0, dayNum(x.timeline[x.timeline.length - 1]!.to))
  const span = Math.max(1, end - start)
  const pos = (iso: string) => ((dayNum(iso) - start) / span) * 100
  return (
    <div className="space-y-1.5">
      <div className="relative h-9 overflow-hidden rounded-lg bg-neutral-100 dark:bg-neutral-800">
        {x.timeline.map((s) => (
          <div key={`${s.state}-${s.from}`} title={`${stateLabel(t, s.state)} · ${fmtDate(s.from)} → ${fmtDate(s.to)} · ${t('days', { n: s.days })}`}
            className={`absolute inset-y-0 border-r border-white/70 ${STATE_BG[s.state]} ${s.source === 'derived' ? 'opacity-70' : ''}`}
            style={{ left: `${pos(s.from)}%`, width: `${Math.max(0.6, pos(s.to) - pos(s.from))}%` }} />
        ))}
        {x.blocks.map((b) => (
          <div key={b.id} title={`${reasonLabel(t, b.reason)} · ${fmtDate(b.startsOn)} → ${b.endsOn ? fmtDate(b.endsOn) : t('stillBlocked')}`}
            className="absolute inset-y-0" style={{ ...STRIPES, left: `${pos(b.startsOn)}%`, width: `${Math.max(0.6, pos(b.endsOn ?? todayIso()) - pos(b.startsOn))}%` }} />
        ))}
        {x.plan.endOn && <div className="absolute inset-y-0 w-0.5 bg-red-600" style={{ left: `${pos(x.plan.endOn)}%` }} title={t('plannedEnd', { date: fmtDate(x.plan.endOn) })} />}
        <div className="absolute inset-y-0 w-0.5 bg-neutral-900 dark:bg-white" style={{ left: `${pos(todayIso())}%` }} />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600 dark:text-neutral-300">
        {x.timeline.map((s) => (
          <span key={`l-${s.state}-${s.from}`} className="inline-flex items-center gap-1.5">
            <span className={`inline-block size-2.5 rounded-full ${STATE_BG[s.state]}`} />
            {stateLabel(t, s.state)} <span className="tabular text-neutral-500">{fmtDate(s.from)} · {t('days', { n: s.days })}{s.blockedDays ? ` · ${t('blockedDays', { n: s.blockedDays })}` : ''}</span>
          </span>
        ))}
        {x.plan.endOn && <span className="inline-flex items-center gap-1.5 text-red-700 dark:text-red-300"><span className="inline-block h-3 w-0.5 bg-red-600" />{t('plannedEnd', { date: fmtDate(x.plan.endOn) })}</span>}
      </div>
    </div>
  )
}

const KIND_ORDER = ['trench_total', 'trench', 'ha', 'ha_m', 'hp_plus', 'hp', 'nha', 'pnha', 'dp', 'crossing']
export function kindLabel(t: ExecT, l: OutputLine) {
  const surf = { earth: t('surfEarth'), slab: t('surfSlab'), paving: t('surfPaving'), asphalt: t('surfAsphalt'), clinker: t('surfClinker'),
    natural_stone: t('surfNatural_stone'), hansegrand: t('surfHansegrand'), other: t('surfOther') } as Record<string, string>
  switch (l.kind) {
    case 'trench': return t('kindTrench', { surface: surf[l.surface ?? 'other'] ?? l.surface ?? '' })
    case 'trench_total': return t('kindTrench_total')
    case 'ha': return t('kindHa'); case 'ha_m': return t('kindHa_m'); case 'hp': return t('kindHp'); case 'hp_plus': return t('kindHp_plus'); case 'nha': return t('kindNha')
    case 'pnha': return t('kindPnha'); case 'dp': return t('kindDp'); case 'crossing': return t('kindCrossing')
    default: return l.kind
  }
}

function OutputTable({ t, lines }: { t: ExecT; lines: OutputLine[] }) {
  const sorted = [...lines].sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || (b.quantity ?? 0) - (a.quantity ?? 0))
  return (
    <div>
      <h3 className="mb-1.5 text-sm font-semibold">{t('outputTitle')}</h3>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
          <th className="py-1.5 font-medium">{t('colWhat')}</th><th className="py-1.5 text-right font-medium">{t('colBuilt')}</th><th className="py-1.5 text-right font-medium">{t('colPlanned')}</th>
        </tr></thead>
        <tbody>
          {sorted.map((l) => (
            <tr key={`${l.kind}-${l.surface}`} className={`border-b border-neutral-100 last:border-0 dark:border-neutral-800/60 ${l.kind === 'trench' ? 'text-neutral-500' : ''}`}>
              <td className={`py-1.5 ${l.kind === 'trench' ? 'pl-3' : 'font-medium'}`}>{kindLabel(t, l)}</td>
              <td className="tabular py-1.5 text-right">{l.quantity != null ? fmtNumber(l.quantity) : <span className="text-xs text-neutral-400">{t('notRecorded')}</span>}</td>
              <td className="tabular py-1.5 text-right">{l.planned != null ? <>{fmtNumber(l.planned)}{l.quantity != null && <span className="text-xs text-neutral-500"> ({Math.round((l.quantity / Math.max(1, l.planned)) * 100)} %)</span>}</> : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function RevenueTable({ t, x }: { t: ExecT; x: ProjectExecution }) {
  const groups = { advance: t('grpAdvance'), progress: t('grpProgress'), extra: t('grpExtra'), damage: t('grpDamage'), penalty: t('grpPenalty'),
    final: t('grpFinal'), other: t('grpOther') } as Record<string, string>
  return (
    <div>
      <h3 className="mb-1.5 text-sm font-semibold">{t('revenueTitle')}</h3>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
          <th className="py-1.5 font-medium">{t('colType')}</th><th className="py-1.5 text-right font-medium">{t('colInvoices')}</th><th className="py-1.5 text-right font-medium">{t('colNet')}</th>
        </tr></thead>
        <tbody>
          {x.revenue.lines.map((l) => (
            <tr key={`${l.group}-${l.type}`} className="border-b border-neutral-100 last:border-0 dark:border-neutral-800/60">
              <td className="py-1.5">{l.type} <span className="text-xs text-neutral-500">· {groups[l.group] ?? l.group}</span></td>
              <td className="tabular py-1.5 text-right">{l.invoices}</td>
              <td className="tabular py-1.5 text-right">{fmtEur(l.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function History({ t, x, admin }: { t: ExecT; x: ProjectExecution; admin: boolean }) {
  const qc = useQueryClient()
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['execution', x.projectId] }); void qc.invalidateQueries({ queryKey: ['portfolio'] }) }
  const delEvent = useMutation({ mutationFn: (id: number) => api.del(`/api/projects/${x.projectId}/state/${id}`), onSuccess: refresh })
  const delBlock = useMutation({ mutationFn: (id: string) => api.del(`/api/projects/${x.projectId}/blocks/${id}`), onSuccess: refresh })
  if (x.events.length === 0 && x.blocks.length === 0) return null
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {x.events.length > 0 && (
        <div>
          <h3 className="mb-1.5 text-sm font-semibold">{t('historyTitle')}</h3>
          <ul className="space-y-1 text-sm">
            {[...x.events].reverse().map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-2">
                <span className="flex flex-wrap items-center gap-2"><StateChip state={e.state} /> <span className="tabular">{fmtDate(e.at)}</span>
                  <span className="text-xs text-neutral-500">{[e.note, e.byName && t('by', { name: e.byName })].filter(Boolean).join(' · ')}</span></span>
                {admin && <button type="button" onClick={() => delEvent.mutate(e.id)} className="text-xs text-red-600 hover:underline">{t('delete')}</button>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {x.blocks.length > 0 && (
        <div>
          <h3 className="mb-1.5 text-sm font-semibold">{t('blocksTitle')}</h3>
          <ul className="space-y-1 text-sm">
            {[...x.blocks].reverse().map((b) => (
              <li key={b.id} className="flex items-center justify-between gap-2">
                <span className="flex flex-wrap items-center gap-2"><span className="inline-block h-3 w-5 rounded-sm" style={STRIPES} />
                  <b>{reasonLabel(t, b.reason)}</b> <span className="tabular">{fmtDate(b.startsOn)} → {b.endsOn ? fmtDate(b.endsOn) : t('stillBlocked')}</span>
                  {b.note && <span className="text-xs text-neutral-500">{b.note}</span>}</span>
                {admin && <button type="button" onClick={() => delBlock.mutate(b.id)} className="text-xs text-red-600 hover:underline">{t('delete')}</button>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ---- dialogs ---------------------------------------------------------------------------------------

function useSave(projectId: string, onClose: () => void) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  return {
    error,
    done: () => { void qc.invalidateQueries({ queryKey: ['execution', projectId] }); void qc.invalidateQueries({ queryKey: ['portfolio'] }); onClose() },
    fail: (e: unknown) => setError(e instanceof ApiError ? e.message : String(e)),
  }
}

function StateDialog({ t, projectId, current, onClose }: { t: ExecT; projectId: string; current: State | null; onClose: () => void }) {
  const s = useSave(projectId, onClose)
  const next = current ? STATES[Math.min(STATES.indexOf(current) + 1, 7)]! : 'offer'
  const [state, setState] = useState<State>(next)
  const [at, setAt] = useState(todayIso())
  const [note, setNote] = useState('')
  const save = useMutation({ mutationFn: () => api.post(`/api/projects/${projectId}/state`, { state, at, note: note.trim() || null }), onSuccess: s.done, onError: s.fail })
  return (
    <Dialog title={t('changeState')} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton disabled={save.isPending} onClick={() => save.mutate()}>{t('save')}</PrimaryButton></>}>
      <Field label={t('state')}>
        <div className="flex flex-wrap gap-1.5">
          {STATES.map((st) => (
            <button key={st} type="button" onClick={() => setState(st)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${state === st ? `${STATE_BG[st]} text-white` : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200'}`}>
              {stateLabel(t, st)}
            </button>
          ))}
        </div>
      </Field>
      <Field label={t('at')}><input type="date" value={at} max={todayIso()} onChange={(e) => setAt(e.target.value)} className={INPUT} /></Field>
      <Field label={t('note')}><input value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} className={INPUT} /></Field>
      {s.error && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{s.error}</p>}
    </Dialog>
  )
}

function BlockDialog({ t, projectId, onClose }: { t: ExecT; projectId: string; onClose: () => void }) {
  const s = useSave(projectId, onClose)
  const [reason, setReason] = useState<Reason>('permit')
  const [from, setFrom] = useState(todayIso())
  const [note, setNote] = useState('')
  const save = useMutation({ mutationFn: () => api.post(`/api/projects/${projectId}/blocks`, { startsOn: from, reason, note: note.trim() || null }), onSuccess: s.done, onError: s.fail })
  return (
    <Dialog title={t('block')} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton disabled={save.isPending} onClick={() => save.mutate()}>{t('save')}</PrimaryButton></>}>
      <Field label={t('reason')}>
        <div className="flex flex-wrap gap-1.5">
          {REASONS.map((r) => (
            <button key={r} type="button" onClick={() => setReason(r)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${reason === r ? 'bg-red-600 text-white' : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200'}`}>
              {reasonLabel(t, r)}
            </button>
          ))}
        </div>
      </Field>
      <Field label={t('startsOn')}><input type="date" value={from} max={todayIso()} onChange={(e) => setFrom(e.target.value)} className={INPUT} /></Field>
      <Field label={t('note')}><input value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} className={INPUT} /></Field>
      {s.error && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{s.error}</p>}
    </Dialog>
  )
}

function EndBlockDialog({ t, projectId, blockId, starts, onClose }: { t: ExecT; projectId: string; blockId: string; starts: string; onClose: () => void }) {
  const s = useSave(projectId, onClose)
  const [to, setTo] = useState(todayIso())
  const save = useMutation({ mutationFn: () => api.post(`/api/projects/${projectId}/blocks/${blockId}/end`, { endsOn: to }), onSuccess: s.done, onError: s.fail })
  return (
    <Dialog title={t('endBlock')} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton disabled={save.isPending} onClick={() => save.mutate()}>{t('save')}</PrimaryButton></>}>
      <Field label={t('endsOn')}><input type="date" value={to} min={starts} max={todayIso()} onChange={(e) => setTo(e.target.value)} className={INPUT} /></Field>
      {s.error && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{s.error}</p>}
    </Dialog>
  )
}

function PlanDialog({ t, x, onClose }: { t: ExecT; x: ProjectExecution; onClose: () => void }) {
  const s = useSave(x.projectId, onClose)
  const own = (k: string) => x.plan.sources[k] === 'app'
  const init = (k: keyof ProjectExecution['plan']) => (own(k as string) && x.plan[k] != null ? String(x.plan[k]) : '')
  const [f, setF] = useState<Record<string, string>>({
    contractValue: init('contractValue'), startOn: init('startOn'), endOn: init('endOn'), activationOn: init('activationOn'), acceptanceOn: init('acceptanceOn'),
    hp: init('hp'), hpPlus: init('hpPlus'), ha: init('ha'), trenchM: init('trenchM'),
  })
  const num = (v: string) => (v.trim() === '' ? null : Number(v.replace(',', '.')))
  const save = useMutation({
    mutationFn: () => api.put(`/api/projects/${x.projectId}/plan`, {
      contractValue: num(f.contractValue!), startOn: f.startOn || null, endOn: f.endOn || null, activationOn: f.activationOn || null,
      acceptanceOn: f.acceptanceOn || null, hp: num(f.hp!), hpPlus: num(f.hpPlus!), ha: num(f.ha!), trenchM: num(f.trenchM!),
    }),
    onSuccess: s.done, onError: s.fail,
  })
  const ph = (k: keyof ProjectExecution['plan']) => (x.plan.sources[k as string] === 'au' && x.plan[k] != null ? `${String(x.plan[k])} (${t('fromAu')})` : '')
  const input = (k: string, label: string, type: 'date' | 'number', pk: keyof ProjectExecution['plan']) => (
    <Field label={label}><input type={type === 'date' ? 'date' : 'text'} inputMode={type === 'number' ? 'decimal' : undefined} value={f[k]} placeholder={ph(pk)}
      onChange={(e) => setF({ ...f, [k]: e.target.value })} className={INPUT} /></Field>
  )
  return (
    <Dialog title={t('editPlan')} hint={t('planHint')} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton disabled={save.isPending} onClick={() => save.mutate()}>{t('save')}</PrimaryButton></>}>
      {input('contractValue', t('planContract'), 'number', 'contractValue')}
      <div className="grid gap-3 sm:grid-cols-2">
        {input('startOn', t('planStart'), 'date', 'startOn')}
        {input('endOn', t('planEnd'), 'date', 'endOn')}
        {input('activationOn', t('planActivation'), 'date', 'activationOn')}
        {input('acceptanceOn', t('planAcceptance'), 'date', 'acceptanceOn')}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {input('hp', t('planHp'), 'number', 'hp')}
        {input('hpPlus', t('planHpPlus'), 'number', 'hpPlus')}
        {input('ha', t('planHa'), 'number', 'ha')}
        {input('trenchM', t('planTrench'), 'number', 'trenchM')}
      </div>
      {s.error && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{s.error}</p>}
    </Dialog>
  )
}
